import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { TSelectOption } from 'src/app/project/components/input/select/select.model';
import { UsersService } from 'src/app/project/modules/users/users.service';
import { TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { ActiveService } from 'src/app/project/services/active/active.service';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { EAdvancedFilterOptions } from '../../../../models/advanced-filter-options.enum';
import {
  TAdvancedFilterAssignee,
  TAdvancedFilterCreatedBy,
  TAdvancedFilterSingle,
  TFilterAssigneeOptions,
  TFilterCreatedByOptions,
} from '../../../../models/advanced-filter.model';
import {
  assigneeOperatorOptions,
  createdByOperatorOptions,
} from '../../../../utils/advanced-filter-operator.utils';

@Component({
  selector: 'pp-advanced-filter-users',
  templateUrl: './advanced-filter-users.component.html',
  styleUrls: ['./advanced-filter-users.component.scss'],
  standalone: false,
})
export class AdvancedFilterUserComponent implements OnChanges, OnDestroy {
  @Input() ppFilter: TAdvancedFilterSingle;
  @Input() ppIndex: number;
  @Input() ppType: 'assignees' | 'created_by';
  @Output() ppFilterChange = new EventEmitter<TAdvancedFilterSingle>();

  value: string[];
  option: TFilterCreatedByOptions | TFilterAssigneeOptions;
  filter: TAdvancedFilterCreatedBy | TAdvancedFilterAssignee;

  advancedFilterUserOptions: TSelectOption[];
  EAdvancedFilterOptions = EAdvancedFilterOptions;
  private workspaces$ = new Observable<TWorkspacesById>();
  private readonly destroy$ = new Subject<void>();
  workspaceUsers: string[];

  constructor(
    private store: Store<{
      workspaces: TWorkspacesById;
    }>,
    private activeService: ActiveService,
    private usersService: UsersService,
  ) {
    this.workspaces$ = this.store.pipe(select(EStore.WORKSPACES));

    this.workspaces$.pipe(takeUntil(this.destroy$)).subscribe((workspaces) => {
      const workspaceId = this.activeService.getActiveWorkspaceId();
      const workspace = workspaces[workspaceId];

      if (workspace) {
        this.workspaceUsers = workspace.users;
      } else {
        this.workspaceUsers = Object.keys(this.usersService.getUsers());
      }
    });
  }

  ngOnChanges(): void {
    this.advancedFilterUserOptions =
      this.ppType === 'created_by' ? createdByOperatorOptions : assigneeOperatorOptions;

    if (this.ppFilter && !this.ppFilter?.value) {
      this.ppFilter.value = [];
    }

    this.filter = this.ppFilter as TAdvancedFilterCreatedBy | TAdvancedFilterAssignee;

    this.value = this.filter.value;
    this.option = this.filter.option;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  changeFieldCondition(newCondition: TSelectOption): void {
    const newValue = newCondition.value as TFilterCreatedByOptions | TFilterAssigneeOptions;
    if (
      (newValue === EAdvancedFilterOptions.IS || newValue === EAdvancedFilterOptions.IS_NOT) &&
      this.filter.value.length
    ) {
      this.filter.value.length = 1;
    }

    this.filter.option = newValue;
    this.option = newValue;

    this.filter.operator = newCondition.operator;

    if (Array.isArray(this.filter.value?.[0])) {
      this.filter.value = [];
    }

    if (
      this.ppFilter.option === EAdvancedFilterOptions.IS_EMPTY ||
      this.ppFilter.option === EAdvancedFilterOptions.IS_NOT_EMPTY
    ) {
      this.ppFilter.value = newCondition.data;
    }

    this.applyChanges();
  }

  applyChanges(): void {
    this.ppFilterChange.emit(this.filter);
  }

  addUserId(userId: string): void {
    if (
      this.option === EAdvancedFilterOptions.IS_ANY_OF ||
      this.option === EAdvancedFilterOptions.IS_NONE_OF ||
      this.option === EAdvancedFilterOptions.IS_ALL_OF
    ) {
      this.filter.value.push(userId);
    } else {
      this.filter.value = [userId];
    }

    this.applyChanges();
  }

  removeUserId(userId: string): void {
    this.filter.value = this.filter.value.filter((id) => id !== userId);
    this.applyChanges();
  }

  clearUsers(): void {
    this.filter.value = [];
    this.applyChanges();
  }
}
